import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Button";
import Field from "../../../components/Field";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Text from "../../../components/Inputs/Text";
import NumberInput from "../../../components/Inputs/NumberInput";
import { generateErrorsConfigForForm } from "../../../utils/helpers/errors";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { useCompany } from "../../../utils/hooks/company";
import Select from "../../../components/Inputs/Select";

function TimeOffForm({ mode, close, values, error, loading, create, update, transfer }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const company = useCompany();

	const timezone = useMemo(
		() => company?.settings?.timezone?.value,
		[company?.settings?.timezone?.value],
	);

	const onFinish = useCallback((data) => {
		data.plan = data.plan.id;
		data.amount = Number(data.amount) || 0;
		if (mode === "edit" && values) {
			update(values.id, { id: values.id, ...data }, close);
		} else if (mode === "transfer") {
			transfer(values.id, data)
		} else {
			create(data, close);
		}
	}, [mode, values, update, close, transfer, create]);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			plan: mode !== "transfer" ? values?.plan : undefined,
			amount: mode === "transfer" ? ((values?.bucket?.available || 0 ) + (values?.bucket?.pending)): 0,
			...(mode === 'edit' ? {
				addOrRemove: "add",
			} : {}),
			comment: "",
		});
	}, [form, values, mode]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(["data"], error);
		form.setFields(fieldErrors);
	}, [form, error]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			scrollToFirstError
		>
			<Field
				name="plan"
				label={mode === "transfer" ? t("transfer-to-plan") : t("plan")}
				rules={[
					{
						required: true,
						message: t("required-plan"),
					},
					...(mode === "transfer" ? [{
						validator: (_, value) => {
							if (value && value.id === values?.plan?.id) {
								return Promise.reject(new Error(t("transfer-to-same-plan")));
							}
							return Promise.resolve();
						},
					}] : []),
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/timeoff?status=active"
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("plan")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					disabled={mode === "edit"}
				/>
			</Field>
			{mode === "edit" && (
				<Field
					name="addOrRemove"
					label={t("choose-your-action")}
					rules={[
						{
							required: true,
							message: t("this-field-is-required"),
						},
					]}
				>
					<Select
						placeholder={t("choose-your-action")}
						options={[
							{ label: t("add-amount"), value: "add" },
							{ label: t("remove-amount"), value: "remove" },
						]}
					/>
				</Field>
			)}

			<BaseField shouldUpdate>
				{({}, {}, { getFieldValue, setFieldValue }) => {
					const maxBalance = getFieldValue(["plan", "maxBalance"]);
					return (
						<Field
							name="amount"
							label={mode === "transfer" ? t("transfer-amount") : t("amount")}
							rules={[
								{
									required: true,
									message: t("amount-is-required"),
								},
							]}
						>
							<NumberInput
								placeholder={t("amount")}
								disabled={mode === "transfer"}
								/*
								onChange={(e) => {
									let value = Number(e.target.value) || 0;
									if (maxBalance !== 0 && !maxBalance) {
										setFieldValue("amount", value);
										return;
									}
									if (maxBalance < value) {
										value = maxBalance;
									}
									console.log(maxBalance, value);
									setFieldValue("amount", value);
								}}
								*/
							/>
						</Field>
					);
				}}
			</BaseField>
				{ values && (
					<Field
						name="comment"
						label={t("comment")}
					>
						<Text placeholder={t("comment")} />
					</Field>
				)}
			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={loading}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={loading}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					{t("save")}
				</Button>
			</div>
		</Form>
	);
}

export default TimeOffForm;
